/**
 * scrollableTables
 * ========
 *
 * Wrap the tables inside the container with an additional layout,
 * make it scrollable (horizontally)
 *
 * @example
 * <div
 *    data-clientside-hook="scrollableTables"    <-- KEY_TAG Enable scrollable tables in the content
 *    data-scrollable-classname="%CLASS_NAME%"   <-- CLASS_NAME to be added to each table
 * >
 *    Any content <!-- will be kept on the same position -->
 *    <table />   <!-- table gets %CLASS_NAME% class, will be wrapped with additional container -->
 *    Any content <!-- will be kept on the same position -->
 * </div>
 *
 * @returns {[HTMLElement, HTMLTableElement[]][]} Array of arrays,
 *          each item consists of processed table container and its tables list
 */
export default () => {
  const HOOK_NAME = 'scrollableTables'
  const SCROLLABLE_CLASSNAME_HOOK_NAME = 'data-scrollable-classname'
  const WRAPPER_CLASSNAME = 'u-scrollable'

  const getTableContainers = () => {
    return [
      ...document.querySelectorAll(`[data-clientside-hook~="${HOOK_NAME}"]`)
    ]
  }

  const enableScrollbar = wrapper => {
    wrapper.classList.add(WRAPPER_CLASSNAME)

    return wrapper
  }

  const wrapTable = table => {
    const wrapper = document.createElement('div')

    table.parentNode.insertBefore(wrapper, table)
    wrapper.appendChild(table)

    return wrapper
  }

  /**
   * Overwrites table's 'width' attribute which could be set in the content.
   * Fits table into parent container.
   */
  const adjustTableWidth = table => {
    table.setAttribute('width', '100%')
    table.removeAttribute('cellPadding')
    table.removeAttribute('cellSpacing')
    table.removeAttribute('border')
  }

  const processTable = (table, appendClass) => {
    enableScrollbar(wrapTable(table))
    adjustTableWidth(table)

    if (appendClass) {
      table.classList.add(appendClass)
    }

    return table
  }

  const reduceTableContainers = (acc, tableContainer) => {
    const tables = tableContainer.querySelectorAll('table')
    const appendClass = tableContainer.getAttribute(
      SCROLLABLE_CLASSNAME_HOOK_NAME
    )

    if (tables) {
      acc.push([
        tableContainer,
        [...tables].map(table => processTable(table, appendClass))
      ])
    }

    return acc
  }

  return getTableContainers().reduce(reduceTableContainers, [])
}
